import dayjs from 'dayjs'
import { Formik } from 'formik'
import { Alert, Button, Card, Form, Modal, Spinner } from 'react-bootstrap'
import { useParams, useSearchParams } from 'react-router-dom'
import * as yup from 'yup'
import { VField } from '../../components/Fields'
import { useFormProposta, useSaveFormProposta } from '../../hooks/formProposta'
import { getLabelTipoBilancia } from '../../utils/utils'
import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import ModalConfirm from '../../components/ModalConfirm'
import { useState } from 'react'

const schema = yup.object().shape({
  accettazione: yup.boolean(),
  sede: yup.object().when('accettazione', {
    is: true,
    then: (schema) =>
      schema.shape({
        codice_sdi: yup.string().required('Codice SDI è obbligatorio'),
        nome_referente: yup.string().required('Nome referente è obbligatorio'),
        numero_rea: yup.string().required('Numero REA è obbligatorio'),
      }),
    otherwise: (schema) => schema,
  }),
  bilance_selezionate: yup.array().when('accettazione', {
    is: true,
    then: (schema) => schema.of(yup.number()).min(1, 'Selezionare almeno una bilancia'),
    otherwise: (schema) => schema.notRequired(),
  }),
})

export function FormProposta() {
  const { id: idProposta } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const tokenCliente = searchParams.get('token') || ''

  const [confirmModalState, confirmModalActions] = useModalTrigger()
  const [finalModalState, finalModalActions] = useModalTrigger()
  const [filled, setFilled] = useState(false)

  const { data: proposta, isLoading, error } = useFormProposta(idProposta!, tokenCliente)
  const { mutateAsync } = useSaveFormProposta(idProposta!, tokenCliente)

  const bilanceSorted = proposta?.bilance.sort((a, b) => {
    let aDate = a.data_scadenza_taratura
    let bDate = b.data_scadenza_taratura
    if (proposta.tipo_intervento === 'verifica_periodica') {
      aDate = a.data_scadenza_periodica
      bDate = b.data_scadenza_periodica
    }
    if (!aDate && bDate) return 1 // If aDate is undefined, bDate is considered "less than"
    if (aDate && !bDate) return -1 // If bDate is undefined, aDate is considered "greater than"
    if (!aDate || !bDate) return 0 // Handle cases where dates might be undefined
    return aDate.localeCompare(bDate) // Compare dates
  })

  return (
    <div className="w-100 h-100">
      <div className="d-flex align-items-center justify-content-between w-100 position-fixed left-0 top-0 right-0 topbar">
        <div className="d-flex align-items-center">
          <img src="/statik/main/LOGO.svg" className="d-block mx-auto" height={40} alt="" />
        </div>
      </div>
      <div className="page">
        {isLoading && (
          <div>
            <Spinner animation="border" />
          </div>
        )}
        {!filled && !isLoading && error && (
          <Alert variant="danger mt-5">{(error as any)?.data?.detail || (error as any)?.message}</Alert>
        )}
        {!filled && !isLoading && proposta && (
          <Formik
            initialValues={{
              ...proposta,
              accettazione: true,
              bilance_selezionate: [] as number[],
            }}
            validationSchema={schema}
            validateOnMount
            onSubmit={(values, actions) => {
              console.log('Submitting form with values:', values)
              return mutateAsync(values).then(() => {
                setFilled(true)
                finalModalActions.open(values.accettazione ? 'accept' : 'reject')
              })
            }}
          >
            {(formik) => {
              return (
                <form onSubmit={formik.handleSubmit}>
                  <div className="row pt-4">
                    <div className="col-12">
                      {proposta.tipo_intervento === 'verifica_periodica' && (
                        <h1>Richiesta di verificazione periodica</h1>
                      )}
                      {proposta.tipo_intervento === 'rapporto_taratura' && <h1>Richiesta di controllo qualità</h1>}
                      {/* <p className="fst-italic">
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit,
                        sed do eiusmod tempor incididunt ut labore et dolore
                        magna aliqua.
                      </p> */}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 col-xl-6 mb-3">
                      <Card>
                        <Card.Body>
                          <h4>Dati dell'intestatario</h4>
                          <div className="row">
                            <div className="col-12 col-md-6">
                              <VField name="sede.ragione_sociale" label="Titolare dello strumento" readonly />
                            </div>
                            <div className="col-12 col-md-6">
                              <VField
                                name="sede.codice_sdi"
                                label="Codice SDI"
                                readonly={proposta.sede.codice_sdi !== ''}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-12">
                              <VField name="sede.indirizzo_completo" label="Indirizzo dell'intervento" readonly />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-12 col-md-6">
                              <VField name="sede.telefono" label="Numero di telefono" readonly />
                            </div>
                            <div className="col-12 col-md-6">
                              <VField
                                name="sede.nome_referente"
                                label="Nome referente"
                                readonly={proposta.sede.nome_referente !== ''}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-12 col-md-6">
                              <VField
                                name="sede.numero_rea"
                                label="Numero REA"
                                readonly={proposta.sede.numero_rea !== ''}
                              />
                            </div>
                            <div className="col-12 col-md-6"></div>
                          </div>
                          <div className="row mt-4">
                            <div className="col-12">
                              <VField
                                name="preferenze_orari"
                                label="Disponibilità esecuzione intervento"
                                as="textarea"
                                style={{ height: '8em' }}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-12">
                              <VField name="note" label="Note" as="textarea" style={{ height: '8em' }} />
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                    <div className="col-12 col-xl-6 mb-3">
                      <Card>
                        <Card.Body>
                          <h4>Selezione delle bilance</h4>
                          <table className="table table-bottaro">
                            <thead>
                              <tr>
                                <th>
                                  <Form.Check
                                    type="checkbox"
                                    name="bilance_selezionate"
                                    checked={formik.values.bilance_selezionate.length === proposta.bilance.length}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        formik.setFieldValue(
                                          'bilance_selezionate',
                                          proposta.bilance.map((bilancia) => bilancia.id)
                                        )
                                      } else {
                                        formik.setFieldValue('bilance_selezionate', [])
                                      }
                                    }}
                                  />
                                </th>
                                <th>Matricola</th>
                                <th>Tipo</th>
                                <th>Reparto</th>
                                <th>Scadenza</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bilanceSorted?.map((bilancia) => {
                                const refDate =
                                  proposta.tipo_intervento === 'verifica_periodica'
                                    ? bilancia.data_scadenza_periodica
                                    : bilancia.data_scadenza_taratura
                                const refDateStr = refDate ? dayjs(refDate).format('DD/MM/YYYY') : 'N/A'
                                return (
                                  <tr key={bilancia.id}>
                                    <td>
                                      <Form.Check
                                        type="checkbox"
                                        name="bilance_selezionate"
                                        checked={formik.values.bilance_selezionate.includes(bilancia.id)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            formik.setFieldValue('bilance_selezionate', [
                                              ...formik.values.bilance_selezionate,
                                              bilancia.id,
                                            ])
                                          } else {
                                            formik.setFieldValue(
                                              'bilance_selezionate',
                                              formik.values.bilance_selezionate.filter((id) => id !== bilancia.id)
                                            )
                                          }
                                        }}
                                      />
                                    </td>
                                    <td>{bilancia.matricola}</td>
                                    <td>{getLabelTipoBilancia(bilancia.tipo_bilancia)}</td>
                                    <td>{bilancia.reparto}</td>
                                    <td>{refDateStr}</td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </Card.Body>
                      </Card>
                      <div className="d-flex justify-content-end mt-3">
                        <Button
                          variant="danger"
                          className="me-2"
                          onClick={() => {
                            formik.setFieldValue('accettazione', false)
                            confirmModalActions.open('reject')
                          }}
                        >
                          RIFIUTA INTERVENTO
                        </Button>
                        <Button
                          variant="success"
                          className=""
                          disabled={!formik.isValid}
                          onClick={() => {
                            formik.setFieldValue('accettazione', true)
                            confirmModalActions.open('accept')
                          }}
                        >
                          ACCETTA INTERVENTO
                        </Button>
                      </div>
                    </div>
                  </div>
                  <ModalConfirm
                    isOpen={confirmModalState.isOpen}
                    toggle={confirmModalActions.toggle}
                    action={() => {
                      confirmModalActions.close()
                      formik.submitForm()
                    }}
                    body={
                      confirmModalState.value === 'accept'
                        ? 'Cliccando su "Accetta e invia" verrà formalizzata la richiesta di intervento per le bilance selezionate. Vuoi proseguire?'
                        : 'Cliccando su "Rifiuta e invia" la proposta verrà rifiutata e non verrà effettuato alcun intervento.'
                    }
                    title={
                      confirmModalState.value === 'accept'
                        ? "Vuoi richiedere l'intervento?"
                        : "Vuoi rifiutare l'intervento?"
                    }
                    cancelText="Annulla"
                    confirmText={confirmModalState.value === 'accept' ? 'Accetta e invia' : 'Rifiuta e invia'}
                    confirmVariant={confirmModalState.value === 'accept' ? 'success' : 'danger'}
                  />
                </form>
              )
            }}
          </Formik>
        )}
      </div>
      <Modal show={finalModalState.isOpen} centered backdropClassName="backdrop-high">
        <Modal.Header className="">
          <Modal.Title>Operazione completata</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="p-2">
            <span>Le informazioni che hai inserito sono state salvate. Puoi chiudere questa pagina. </span>
            {finalModalState.value === 'accept' && (
              <span>
                Riceverai nei prossimi giorni l'email con il certificato di presa in carico della tua richiesta.
              </span>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer />
      </Modal>
    </div>
  )
}
